import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./jobsV2.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchJobs, updateJob, duplicateJob, clearLatestJob, fetchDashboardStats } from "../../Redux/ActionCreators/jobsAction";
import { getUsers } from "../../Redux/ActionCreators/userAction";
import { Snackbar, Alert } from "@mui/material";

const JOB_STATUS_MAP = {
  Active: "14107328-840b-4ad0-904f-8a2243720f70",
  Draft: "855705f5-9696-46fc-b402-4a18ffd819af",
  Closed: "f6fafc75-8d8e-4d72-a27c-907dd09ac6bf",
};

const JOB_TYPE_IDS = {
  "Part-Time": "64b3c119-597e-4f8a-a66a-b448fc2baf44",
  "Full-Time": "9c4ae6ff-a326-4ddb-936e-042cdd715bc2",
};


const AVATAR_COLORS = ["#7B5EA7", "#F75891", "#2F80ED", "#E86C3A", "#00B8D9", "#4CC948"];

const getInitials = (name) => {
  if (!name) return "?";
  return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  return AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length];
};

const JobsV2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchDebounceTimer = useRef(null);
  const checkedActiveFallback = useRef(false);
  const didAutoSwitchToDraft = useRef(false);
  const hasHandledDraftFallback = useRef(false);
  const prevLoadingRef = useRef(false);

  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("candidates_")) sessionStorage.removeItem(key);
    });
  }, []);

  const initialTab = location.state?.defaultTab || "Active";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [tabCounts, setTabCounts] = useState({ Active: 0, Draft: 0, Closed: 0 });

  const SESSION_PAGE_SIZE_KEY = useMemo(() => `jobs_pageSize_${activeTab}`, [activeTab]);
  const SESSION_CURRENT_PAGE_KEY = useMemo(() => `jobs_currentPage_${activeTab}`, [activeTab]);

  const safeGet = (key, fallback) => {
    try {
      const value = Number(window?.sessionStorage?.getItem(key));
      return Number.isFinite(value) && value > 0 ? value : fallback;
    } catch {
      return fallback;
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ createdBy: "", jobType: "", addedPeriod: "" });
  const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
  const [confirmModal, setConfirmModal] = useState({ open: false, action: "", jobId: null });
  const [openMenuId, setOpenMenuId] = useState(null);
  const [menuDirection, setMenuDirection] = useState("down");

  const { jobList = [], pagination } = useSelector((state) => state.jobs || {});
  const totalPages = Math.ceil((pagination?.total || 0) / pageSize);
  const loading = useSelector((state) => state.jobs.loading);
  const users = useSelector((state) => state.users?.usersList || []);
  const dashboardStats = useSelector((state) => state.jobs.dashboardStats);
  const dashboardStatsLoading = useSelector((state) => state.jobs.dashboardStatsLoading);

  const visibleJobIds = jobList?.map((job) => job.id) || [];
  const isAllSelected = visibleJobIds.length > 0 && visibleJobIds.every((id) => selectedJobs.includes(id));

  const getFiltersAsParams = () => {
    const jobTypeId = JOB_TYPE_IDS[filters.jobType] || "";
    let fromDate = "", toDate = "";
    if (filters.addedPeriod) {
      toDate = new Date().toISOString().split("T")[0];
      const from = new Date();
      from.setDate(from.getDate() - parseInt(filters.addedPeriod));
      fromDate = from.toISOString().split("T")[0];
    }
    return { createdBy: filters.createdBy, jobTypeId, fromDate, toDate, search: searchTerm };
  };

  const refreshList = () => {
    const { createdBy, jobTypeId, fromDate, toDate, search } = getFiltersAsParams();
    dispatch(fetchJobs(currentPage, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, search));
  };

  useEffect(() => {
    const { createdBy, jobTypeId, fromDate, toDate, search } = getFiltersAsParams();
    dispatch(fetchJobs(currentPage, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, search));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, activeTab, currentPage, filters, pageSize]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
  }, [pageSize, SESSION_PAGE_SIZE_KEY]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
  }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

  useEffect(() => {
    if (dashboardStats) {
      setTabCounts({
        Active: dashboardStats.total_active_jobs ?? 0,
        Draft: dashboardStats.draft_jobs ?? 0,
        Closed: dashboardStats.closed_jobs ?? 0,
      });
    }
  }, [dashboardStats]);

  useEffect(() => {
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;
    if (loading || !wasLoading) return;

    if (activeTab === "Active" && !checkedActiveFallback.current) {
      checkedActiveFallback.current = true;
      if (jobList.length === 0) {
        didAutoSwitchToDraft.current = true;
        setActiveTab("Draft");
        setCurrentPage(1);
      } else {
        dispatch({ type: "SET_HAS_ANY_JOBS", payload: true });
      }
    } else if (activeTab === "Draft" && didAutoSwitchToDraft.current && !hasHandledDraftFallback.current) {
      hasHandledDraftFallback.current = true;
      if (jobList.length === 0) {
        dispatch({ type: "SET_HAS_ANY_JOBS", payload: false });
        navigate("/jobs/createjob");
      } else {
        dispatch({ type: "SET_HAS_ANY_JOBS", payload: true });
      }
    }
  }, [loading, jobList.length, activeTab, dispatch, navigate]);

  useEffect(() => {
    setSelectedJobs((prev) => prev.filter((id) => jobList.some((job) => job.id === id)));
  }, [jobList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".jv2-qa-more-wrap")) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedJobs([]);
  }, [currentPage, searchTerm, activeTab, filters]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setFilters({ createdBy: "", jobType: "", addedPeriod: "" });
    setSearchTerm("");
    sessionStorage.removeItem(`jobs_pageSize_${activeTab}`);
    sessionStorage.removeItem(`jobs_currentPage_${activeTab}`);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    clearTimeout(searchDebounceTimer.current);
    if (value.length === 0) {
      const { createdBy, jobTypeId, fromDate, toDate } = getFiltersAsParams();
      dispatch(fetchJobs(1, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, ""));
      setCurrentPage(1);
      return;
    }
    if (value.length >= 3) {
      searchDebounceTimer.current = setTimeout(() => {
        const { createdBy, jobTypeId, fromDate, toDate } = getFiltersAsParams();
        dispatch(fetchJobs(1, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, value));
        setCurrentPage(1);
      }, 700);
    }
  };

  const handleFilterChange = (filter, value) => {
    setFilters((prev) => ({ ...prev, [filter]: value }));
    setCurrentPage(1);
  };

  const handleSelectJob = (id, checked) => {
    setSelectedJobs((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((jobId) => jobId !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedJobs(checked ? visibleJobIds : []);
  };

  const changeStatus = async (id, statusId, goToTab = null) => {
    dispatch({ type: "GET_JOB_LIST_LOADING" });
    const result = await dispatch(updateJob(id, { job_status_id: statusId }));
    if (statusId === JOB_STATUS_MAP.Active && result?.__error402) {
      setSnackbar({ open: true, message: "Active jobs limit reached.", severity: "warning" });
      dispatch({ type: "CLEAR_LATEST_JOB_DONE" });
      return;
    }
    if (result?.success) {
      setSnackbar({ open: true, message: "Job status updated successfully", severity: "success" });
      dispatch(fetchDashboardStats());
      if (goToTab) setActiveTab(goToTab);
    } else {
      setSnackbar({ open: true, message: "Action failed", severity: "error" });
      dispatch({ type: "CLEAR_LATEST_JOB_DONE" });
    }
  };

  const handlePublish = (id) => changeStatus(id, JOB_STATUS_MAP.Active, "Active");
  const handleCloseJob = (id) => changeStatus(id, JOB_STATUS_MAP.Closed, "Closed");
  const handleEdit = (jobId, restrictedEdit = false) => navigate("/jobs/createjob", { state: { jobId, subStep: 2, restrictedEdit } });

  const toggleMenu = (jobId, e) => {
    if (openMenuId === jobId) {
      setOpenMenuId(null);
      return;
    }
    const btn = e.currentTarget;
    const rect = btn.getBoundingClientRect();
    const container = btn.closest(".jv2-table-outer");
    const boundBottom = container ? container.getBoundingClientRect().bottom : window.innerHeight;
    const estimatedMenuHeight = 130;
    setMenuDirection(boundBottom - rect.bottom < estimatedMenuHeight ? "up" : "down");
    setOpenMenuId(jobId);
  };

  const handleDuplicateJob = async (id) => {
    const result = await dispatch(duplicateJob(id));
    if (result?.success) {
      setSnackbar({ open: true, message: "Job duplicated successfully", severity: "success" });
      refreshList();
      dispatch(fetchDashboardStats());
    } else {
      setSnackbar({ open: true, message: result?.message || "Failed to duplicate job", severity: "error" });
    }
  };

  useEffect(() => {
    dispatch(getUsers("", 1, 100));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  const getDaysAdded = (dateString) => {
    if (!dateString) return { short: "—", full: "" };
    const targetDate = new Date(dateString);
    const today = new Date();
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.round((today - targetDate) / (1000 * 60 * 60 * 24));
    const full = new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    if (diffDays === 0) return { short: "Today", full };
    if (diffDays < 0) return { short: `${Math.abs(diffDays)}d from now`, full };
    return { short: `${diffDays}d`, full };
  };

  const openConfirmModal = (action, jobId) => setConfirmModal({ open: true, action, jobId });

  const handleConfirmAction = () => {
    if (confirmModal.action === "close") handleCloseJob(confirmModal.jobId);
    else if (confirmModal.action === "publish") handlePublish(confirmModal.jobId);
    else if (confirmModal.action === "duplicate") handleDuplicateJob(confirmModal.jobId);
    setConfirmModal({ open: false, action: "", jobId: null });
  };

  const renderPagination = () => {
    const pages = [];
    pages.push(
      <button key="prev" className="jv2-page-btn" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>
        ‹
      </button>
    );
    pages.push(
      <button key={1} className={`jv2-page-btn${currentPage === 1 ? " active" : ""}`} onClick={() => setCurrentPage(1)}>1</button>
    );
    if (currentPage > 3) pages.push(<span key="ldots" className="jv2-page-ellipsis">…</span>);
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      pages.push(
        <button key={i} className={`jv2-page-btn${currentPage === i ? " active" : ""}`} onClick={() => setCurrentPage(i)}>{i}</button>
      );
    }
    if (currentPage < totalPages - 2) pages.push(<span key="rdots" className="jv2-page-ellipsis">…</span>);
    if (totalPages > 1) {
      pages.push(
        <button key={totalPages} className={`jv2-page-btn${currentPage === totalPages ? " active" : ""}`} onClick={() => setCurrentPage(totalPages)}>{totalPages}</button>
      );
    }
    pages.push(
      <button key="next" className="jv2-page-btn" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>
        ›
      </button>
    );
    return pages;
  };

  return (
    <div className="jv2-page">
      {/* Top header */}
      <div className="jv2-top-header">
        <h2 className="jv2-page-title">Jobs</h2>
        <button
          className="jv2-create-btn"
          onClick={() => { dispatch(clearLatestJob()); navigate("/jobs/createjob"); }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/><line x1="5" y1="12" x2="19" y2="12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>
          Create Job
        </button>
      </div>

      {/* Stats cards */}
      <div className="jv2-stats-row">
        <div className="jv2-stat-card">
          <div className="jv2-stat-icon jv2-stat-icon--pink">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="7" width="20" height="14" rx="2" stroke="#F75891" strokeWidth="2"/>
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="#F75891" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jv2-stat-body">
            <span className="jv2-stat-label">Total Active Jobs</span>
            <strong className="jv2-stat-value">
              {dashboardStatsLoading ? <span className="spinner-border spinner-border-sm" role="status" style={{ color: "#F75891" }} /> : (dashboardStats?.total_active_jobs ?? "—")}
            </strong>
          </div>
        </div>

        <div className="jv2-stat-card">
          <div className="jv2-stat-icon jv2-stat-icon--purple">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" stroke="#7B5EA7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16 7 22 7 22 13" stroke="#7B5EA7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="jv2-stat-body">
            <span className="jv2-stat-label">New This Week</span>
            <strong className="jv2-stat-value jv2-stat-value--purple">
              {dashboardStatsLoading ? <span className="spinner-border spinner-border-sm" role="status" style={{ color: "#7B5EA7" }} /> : (dashboardStats?.new_this_week ?? "—")}
            </strong>
          </div>
        </div>

        <div className="jv2-stat-card">
          <div className="jv2-stat-icon jv2-stat-icon--teal">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#00B8D9" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="9" cy="7" r="4" stroke="#00B8D9" strokeWidth="2"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke="#00B8D9" strokeWidth="2" strokeLinecap="round"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75" stroke="#00B8D9" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="jv2-stat-body">
            <span className="jv2-stat-label">Total Candidates</span>
            <strong className="jv2-stat-value jv2-stat-value--teal">
              {dashboardStatsLoading ? <span className="spinner-border spinner-border-sm" role="status" style={{ color: "#00B8D9" }} /> : (dashboardStats?.total_candidates ?? "—")}
            </strong>
          </div>
        </div>

        <div className="jv2-stat-card">
          <div className="jv2-stat-icon jv2-stat-icon--green">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="#4CC948" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="7" r="4" stroke="#4CC948" strokeWidth="2"/>
              <polyline points="16 11 18 13 22 9" stroke="#4CC948" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="jv2-stat-body">
            <span className="jv2-stat-label">Hired This Month</span>
            <strong className="jv2-stat-value jv2-stat-value--green">
              {dashboardStatsLoading ? <span className="spinner-border spinner-border-sm" role="status" style={{ color: "#4CC948" }} /> : (dashboardStats?.hired_this_month ?? "—")}
            </strong>
          </div>
        </div>
      </div>

      {/* Main content card */}
      <div className="jv2-content-card">
        {/* Tabs bar */}
        <div className="jv2-tabs-bar">
          <div className="jv2-tabs-nav">
            {["Active", "Draft", "Closed"].map((tab) => (
              <button
                key={tab}
                className={`jv2-tab-btn${activeTab === tab ? " active" : ""}`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
                {dashboardStatsLoading ? (
                  <span className="jv2-tab-badge jv2-tab-badge--loading">
                    <span className="spinner-border spinner-border-sm" role="status" style={{ width: "10px", height: "10px", borderWidth: "1.5px" }} />
                  </span>
                ) : tabCounts[tab] > 0 ? (
                  <span className="jv2-tab-badge">{tabCounts[tab]}</span>
                ) : null}
              </button>
            ))}
          </div>
          <div className="jv2-tabs-actions">
            <button className="jv2-icon-btn" title="Refresh" onClick={refreshList}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M23 4v6h-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M1 20v-6h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Controls bar */}
        <div className="jv2-controls-bar">
          <div className="jv2-search-wrap">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="jv2-search-icon">
              <circle cx="11" cy="11" r="8" stroke="#8A9199" strokeWidth="2"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65" stroke="#8A9199" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              className="jv2-search-input"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="jv2-filter-group">
            <div className="jv2-select-wrap">
              <select
                className="jv2-filter-select"
                value={filters.createdBy}
                onChange={(e) => handleFilterChange("createdBy", e.target.value)}
              >
                <option value="">Created by</option>
                {users.filter((u) => u?.isActive).map((u) => (
                  <option key={u.userID} value={u.userID}>{u.name}</option>
                ))}
              </select>
              <svg className="jv2-select-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="jv2-select-wrap">
              <select
                className="jv2-filter-select"
                value={filters.jobType}
                onChange={(e) => handleFilterChange("jobType", e.target.value)}
              >
                <option value="">All job type</option>
                {[...new Set(jobList.map((job) => job?.job_type?.name).filter(Boolean))].map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <svg className="jv2-select-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
            <div className="jv2-select-wrap">
              <select
                className="jv2-filter-select"
                value={filters.addedPeriod}
                onChange={(e) => handleFilterChange("addedPeriod", e.target.value)}
              >
                <option value="">All Dates</option>
                <option value="0">Today</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 3 months</option>
                <option value="180">Last 6 months</option>
                <option value="365">Last 1 year</option>
              </select>
              <svg className="jv2-select-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none"><polyline points="6 9 12 15 18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="jv2-table-outer">
          <div className="jv2-table-scroll">
            <table className="jv2-table">
              <thead>
                <tr>
                  <th className="jv2-th-check">
                    <input type="checkbox" checked={isAllSelected} onChange={(e) => handleSelectAll(e.target.checked)} />
                  </th>
                  <th className="jv2-th-title">Job Title</th>
                  <th className="jv2-th-days">Days Added</th>
                  <th className="jv2-th-creator">Created By</th>
                  <th className="jv2-th-stat">Candidates</th>
                  <th className="jv2-th-stat">Shortlisted</th>
                  <th className="jv2-th-stat">Hired</th>
                  <th className="jv2-th-actions">Quick Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="jv2-loading-cell">
                      <div className="spinner-border" role="status" style={{ color: "#F75891", width: "28px", height: "28px" }}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : jobList.length > 0 ? (
                  jobList.map((job) => {
                    if (!job?.id) return null;
                    const { short, full } = getDaysAdded(job.created_at);
                    const creatorName = job.creator?.name || "";
                    const isSelected = selectedJobs.includes(job.id);
                    const candidateCount = job.job_stats?.candidate_count ?? 0;
                    const shortlistedCount = job.job_stats?.shortlisted_count ?? 0;
                    const hiredCount = job.job_stats?.hired_count ?? 0;
                    return (
                      <tr key={job.id} className={isSelected ? "jv2-row-selected" : ""}>
                        <td className="jv2-td-check">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectJob(job.id, e.target.checked)}
                          />
                        </td>
                        <td className="jv2-td-title">
                          <div className="jv2-job-title-cell">
                            <strong
                              className={`jv2-job-name${activeTab !== "Closed" ? " clickable" : ""}`}
                              onClick={() => {
                                if (activeTab === "Draft") handleEdit(job.id);
                                else if (activeTab === "Active") navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id } });
                              }}
                            >
                              {job.title || "—"}
                            </strong>
                            <span className="jv2-job-company">{job.company_name || ""}</span>
                          </div>
                        </td>
                        <td className="jv2-td-days">
                          <div className="jv2-days-cell">
                            <strong>{short}</strong>
                            <span>{full}</span>
                          </div>
                        </td>
                        <td className="jv2-td-creator">
                          <div className="jv2-creator-cell">
                            <span
                              className="jv2-avatar"
                              style={{ backgroundColor: getAvatarColor(creatorName) }}
                            >
                              {getInitials(creatorName)}
                            </span>
                            <span className="jv2-creator-name">{creatorName || "—"}</span>
                          </div>
                        </td>
                        <td
                          className={`jv2-td-stat${activeTab !== "Draft" ? " jv2-td-clickable" : ""}`}
                          onClick={() => {
                            if (activeTab !== "Draft") navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id } });
                          }}
                        >
                          {candidateCount > 0 ? candidateCount : "0"}
                        </td>
                        <td
                          className={`jv2-td-stat${shortlistedCount > 0 ? " jv2-td-clickable" : ""}`}
                          onClick={() => {
                            if (shortlistedCount > 0) navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id, activateFilter: "shortlisted" } });
                          }}
                        >
                          {shortlistedCount > 0 ? shortlistedCount : "0"}
                        </td>
                        <td
                          className={`jv2-td-stat${hiredCount > 0 ? " jv2-td-clickable" : ""}`}
                          onClick={() => {
                            if (hiredCount > 0) navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id, activateFilter: "hired" } });
                          }}
                        >
                          {hiredCount > 0 ? hiredCount : "0"}
                        </td>
                        <td className="jv2-td-actions">
                          <div className="jv2-quick-actions">
                            {activeTab === "Draft" ? (
                              <button className="jv2-qa-btn" title="Edit" onClick={() => handleEdit(job.id)}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                </svg>
                              </button>
                            ) : (
                              <>
                                <button className="jv2-qa-btn" title="View details" onClick={() => navigate(`/jobs/${job.id}/details`, { state: { jobId: job.id } })}>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2"/>
                                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                  </svg>
                                </button>
                                {activeTab === "Active" ? (
                                  <>
                                    <button className="jv2-qa-btn" title="Job settings" onClick={() => navigate(`/jobs/${job.id}/settings`, { state: { jobId: job.id } })}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-settings2 lucide-settings-2" aria-hidden="true" style={{ width: '14px', height: '14px' }}><path d="M14 17H5"></path><path d="M19 7h-9"></path><circle cx="17" cy="17" r="3"></circle><circle cx="7" cy="7" r="3"></circle></svg>
                                    </button>
                                    <div className="jv2-qa-more-wrap">
                                      <button
                                        className="jv2-qa-btn"
                                        title="More actions"
                                        onClick={(e) => toggleMenu(job.id, e)}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                          <circle cx="12" cy="5" r="1.6" fill="currentColor"/>
                                          <circle cx="12" cy="12" r="1.6" fill="currentColor"/>
                                          <circle cx="12" cy="19" r="1.6" fill="currentColor"/>
                                        </svg>
                                      </button>
                                      {openMenuId === job.id && (
                                        <div className={`jv2-qa-dropdown jv2-qa-dropdown--${menuDirection}`}>
                                          <button
                                            className="jv2-qa-dropdown-item"
                                            onClick={() => { setOpenMenuId(null); handleEdit(job.id, true); }}
                                          >
                                            Edit
                                          </button>
                                          <button
                                            className="jv2-qa-dropdown-item"
                                            onClick={() => { setOpenMenuId(null); openConfirmModal("close", job.id); }}
                                          >
                                            Close
                                          </button>
                                          <button
                                            className="jv2-qa-dropdown-item"
                                            onClick={() => { setOpenMenuId(null); openConfirmModal("duplicate", job.id); }}
                                          >
                                            Duplicate
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <button className="jv2-qa-btn jv2-qa-btn--publish" title="Re-publish" onClick={() => openConfirmModal("publish", job.id)}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                      <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="jv2-empty-cell">No jobs found</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && (
            <div className="jv2-pagination">
              <span className="jv2-pagination-info">
                {pagination?.total ? (
                  <>
                    Showing {(pagination.page - 1) * pagination.page_size + 1}–
                    {Math.min(pagination.page * pagination.page_size, pagination.total)} of{" "}
                    <strong>{pagination.total}</strong> jobs
                  </>
                ) : (
                  "Showing 0 jobs"
                )}
              </span>
              <div className="jv2-pagination-right">
                <div className="jv2-page-btns">{renderPagination()}</div>
                <div className="jv2-per-page">
                  <span>Per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(parseInt(e.target.value)); setCurrentPage(1); }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>

      {confirmModal.open && (
        <div className="jv2-modal-overlay">
          <div className="jv2-modal">
            <div className="jv2-modal-head">
              <h3>Job Action</h3>
              <button
                className="jv2-modal-close"
                onClick={() => setConfirmModal({ open: false, action: "", jobId: null })}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="jv2-modal-body">
              <p>
                {confirmModal.action === "close"
                  ? "Are you sure you want to close this job?"
                  : confirmModal.action === "duplicate"
                  ? "Are you sure you want to duplicate this job?"
                  : "Are you sure you want to publish this job again?"}
              </p>
            </div>
            <div className="jv2-modal-footer">
              <button
                className="jv2-modal-cancel"
                onClick={() => setConfirmModal({ open: false, action: "", jobId: null })}
              >
                Cancel
              </button>
              <button className="jv2-modal-confirm" onClick={handleConfirmAction}>
                {confirmModal.action === "close"
                  ? "Close Job"
                  : confirmModal.action === "duplicate"
                  ? "Yes, Duplicate"
                  : "Publish Job Again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsV2;
